import { NgClass } from "@angular/common";
import { Component } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AuthService } from "@ponder-core/auth/auth.service";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { NavigationService } from "@ponder-core/navigation/navigation.service";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { OverauthorizedComponent } from "@ponder-shared/components/overauthorized.component";
import { distinctUntilChanged } from "rxjs";

@Component({
    imports: [ReactiveFormsModule, BusySpinComponent, NgClass, OverauthorizedComponent],
    templateUrl: "./signup.component.html"
})
export class SignUpComponent {
    public signupForm!: FormGroup;
    public isProcessing: boolean = false;
    public processingError?: string;

    public emailError?: string;
    public usernameError?: string;
    public passwordError?: string;

    public constructor(formBuilder: FormBuilder, private events: EventBroker, private authService: AuthService, private navigation: NavigationService) {
        this.signupForm = formBuilder.group({
            email: ["", [Validators.required, Validators.email]],
            username: ["", [Validators.required, Validators.minLength(3), Validators.maxLength(64)]],
            password: ["", [Validators.required, Validators.minLength(8), Validators.maxLength(64)]]
        });

        this.signupForm.get("email")!.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(() => {
                this.emailError = (this.signupForm.get("email")!.valid ? undefined : this.emailError);
                this.processingError = undefined;
            });

        this.signupForm.get("username")!.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(() => {
                this.usernameError = (this.signupForm.get("username")!.valid ? undefined : this.usernameError);
                this.processingError = undefined;
            });

        this.signupForm.get("password")!.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(() => {
                this.passwordError = (this.signupForm.get("password")!.valid ? undefined : this.passwordError);
            });
    }

    public ngOnDestroy(): void {
        this.events.ui.restoreContentPaddingEvent.emit();
    }

    public ngOnInit(): void {
        this.events.ui.hideNavbarEvent.emit();
        this.events.ui.removeContentPaddingEvent.emit();
        
        // v todo remove when leaving restricted beta v
        this.signupForm.disable({ emitEvent: false });
    }

    public onEmailLoseFocus(): void {
        this.emailError = undefined;
        var emailElement = this.signupForm.get("email")!;

        if (!emailElement.valid)
        {
            if ((emailElement.value as string).length == 0)
            {
                this.emailError = "Email address is required.";
            }
            else
            {
                this.emailError = "Invalid email address.";
            }
        }
    }
    
    public onUsernameLoseFocus(): void {
        this.usernameError = undefined;
        var usernameElement = this.signupForm.get("username")!;

        if (!usernameElement.valid)
        {
            if ((usernameElement.value as string).length == 0)
            {
                this.usernameError = "Username is required.";
            }
            else
            {
                this.usernameError = "Username must be between 3 and 64 characters.";
            }
        }
    }

    public onPasswordLoseFocus(): void{
        this.passwordError = undefined;
        var passwordElement = this.signupForm.get("password")!;

        if (!passwordElement.valid)
        {
            if ((passwordElement.value as string).length == 0)
            {
                this.passwordError = "Password is required.";
            }
            else
            {
                this.passwordError = "Password must be between 8 and 64 characters.";
            }
        }
    }

    public canSubmit(): boolean {
        // v todo uncomment when leaving restricted beta v
        return false;//this.signupForm.valid && !this.isProcessing && !this.processingError;
    }

    public async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        this.isProcessing = true;
        this.processingError = undefined;
        this.signupForm.disable({ emitEvent: false });

        const email = this.signupForm.get("email")!.value;
        const username = this.signupForm.get("username")!.value;
        const password = this.signupForm.get("password")!.value;
        
        const response = await this.authService.signupAsync(email, username, password);

        if (!response.error) {
            this.navigation.goto("/signup/confirmation");
        } else {
            this.processingError = response.error.message;
            this.isProcessing = false;
            this.signupForm.enable({ emitEvent: false });
        }
    }
}